import type { Faq, Practice } from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Progress, Spinner, Textarea } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, ExternalLink, EyeOff, Globe, Lightbulb, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useBrandex, useT } from "../context";
import { errorField, errorMessage } from "../errors";
import { draftField, validateStep, type OnboardingDraft, type StepErrors } from "../onboarding/draft";
import { ChambersStep, PracticeStep, QualificationsStep } from "../onboarding/steps";
import { useCurrentOrg } from "../client-scope";
import { keys, usePractice, websiteUrl } from "../queries";
import { practiceToDraft, toChambersRequest, toProfileRequest, validateDetails } from "./profile-draft";
import { ShowcaseCard } from "./showcase";
import { VerificationNotice } from "./website";

const STATE_TONE: Record<Practice["website"]["state"], "success" | "warning" | "neutral"> = {
  live: "success",
  awaiting_activation: "warning",
  awaiting_verification: "warning",
  offline: "warning",
  unpublished: "warning",
  not_included: "neutral",
  needs_onboarding: "neutral",
};

/**
 * Profile and portfolio (F-06): completeness, publish switch, details,
 * portfolio highlights, chambers, the portfolio FAQ and the search
 * visibility checklist.
 */
export function ProfileView() {
  const t = useT();
  const { nav } = useBrandex();
  const orgs = useCurrentOrg();
  const orgId = orgs.orgId;
  const practice = usePractice(orgId);
  if (orgs.isPending || (orgId !== undefined && practice.isPending)) return <Spinner label={t("common.loading")} />;
  if (orgs.isError || practice.isError) return <Alert tone="error">{errorMessage(orgs.error ?? practice.error, t)}</Alert>;
  if (orgId === undefined || !practice.data) {
    return (
      <div className="mx-auto grid max-w-3xl gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("profile.title")}</h1>
        <Alert tone="info">{t("website.state.needs_onboarding")}</Alert>
        <div><Button onClick={() => nav.navigate("/onboarding")}>{t("onboarding.continue")}</Button></div>
      </div>
    );
  }
  const p = practice.data;
  return (
    <div className="mx-auto grid max-w-4xl gap-6">
      <StatusHeader orgId={orgId} practice={p} />
      <div className="grid gap-6 lg:grid-cols-[1fr_18rem] lg:items-start">
        <div className="grid gap-6">
          {/* Remount the forms when saved data changes so they show it. */}
          <DetailsCard key={`d-${p.profile.full_name}-${p.profile.bio}-${p.profile.designation}`} orgId={orgId} practice={p} />
          <ShowcaseCard orgId={orgId} practice={p} />
          <ChambersCard key={`c-${p.chambers.map((c) => c.id).join()}`} orgId={orgId} practice={p} />
          <FaqCard orgId={orgId} />
        </div>
        <div className="grid gap-6">
          <CompletenessCard practice={p} />
          <VisibilityCard practice={p} />
        </div>
      </div>
    </div>
  );
}

function usePracticeUpdate<T>(orgId: string, send: (body: T) => Promise<Practice | undefined>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: T) => {
      const data = await send(body);
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    onSuccess: (data) => qc.setQueryData(keys.practice(orgId), data),
  });
}

function StatusHeader({ orgId, practice }: { orgId: string; practice: Practice }) {
  const t = useT();
  const { api, platform, siteUrl } = useBrandex();
  const { website, completeness } = practice;
  const url = websiteUrl(website, siteUrl);
  const toggle = usePracticeUpdate<"published" | "unpublished">(orgId, async (visibility) =>
    (await api.http.PUT("/v1/orgs/{org_id}/practice/visibility", { params: { path: { org_id: orgId } }, body: { visibility } })).data,
  );
  const canToggle = website.state === "live" || website.state === "unpublished";
  return (
    <header className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("profile.title")}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Globe className="h-4 w-4" aria-hidden />
            <span className="break-all font-mono">{url}</span>
            <Badge tone={STATE_TONE[website.state]}>{t(`website.state.${website.state}` as MessageKey)}</Badge>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {website.state === "live" ? (
            <Button variant="outline" size="sm" onClick={() => void platform.openExternal(url)}>
              <ExternalLink className="h-4 w-4" aria-hidden />
              {t("profile.preview")}
            </Button>
          ) : null}
          {canToggle ? (
            <Button
              variant={website.state === "live" ? "ghost" : "accent"}
              size="sm"
              loading={toggle.isPending}
              onClick={() => toggle.mutate(website.state === "live" ? "unpublished" : "published")}
            >
              {website.state === "live" ? <EyeOff className="h-4 w-4" aria-hidden /> : <Globe className="h-4 w-4" aria-hidden />}
              {t(website.state === "live" ? "profile.unpublish" : "profile.publish")}
            </Button>
          ) : null}
        </div>
      </div>
      {website.state === "unpublished" ? <Alert tone="info">{t("profile.unpublishedHint")}</Alert> : null}
      {toggle.isError ? <Alert tone="error">{errorMessage(toggle.error, t)}</Alert> : null}
      {practice.profile.verification.status === "verified" ? null : <VerificationNotice practice={practice} compact />}
      <div className="grid gap-1">
        <Progress value={completeness.percent} label={t("profile.complete", { percent: completeness.percent })} />
        <p className="text-sm text-muted-foreground">{t("profile.complete", { percent: completeness.percent })}</p>
      </div>
    </header>
  );
}

function SaveRow({ pending, saved, error, label }: { pending: boolean; saved: boolean; error: unknown; label: string }) {
  const t = useT();
  return (
    <div className="grid gap-3">
      {error ? <Alert tone="error">{errorMessage(error, t)}</Alert> : null}
      {saved ? <Alert tone="success">{t("profile.saved")}</Alert> : null}
      <div><Button type="submit" loading={pending}>{label}</Button></div>
    </div>
  );
}

/** Maps a server validation error onto the form's field keys. */
function serverErrors(error: unknown): StepErrors {
  const field = errorField(error);
  return field === undefined ? {} : { [draftField(field)]: "onboarding.error.server" };
}

function DetailsCard({ orgId, practice }: { orgId: string; practice: Practice }) {
  const t = useT();
  const { api } = useBrandex();
  const [draft, setDraft] = useState<OnboardingDraft>(() => practiceToDraft(practice));
  const [errors, setErrors] = useState<StepErrors>({});
  const save = usePracticeUpdate<OnboardingDraft>(orgId, async (d) =>
    (await api.http.PUT("/v1/orgs/{org_id}/practice/profile", { params: { path: { org_id: orgId } }, body: toProfileRequest(d) })).data,
  );
  const update = (patch: Partial<OnboardingDraft>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.details")}</CardTitle>
        <CardDescription>{t("profile.detailsHint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          noValidate
          className="grid gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            const v = validateDetails(draft);
            setErrors(v);
            if (Object.keys(v).length === 0) save.mutate(draft, { onError: (err) => setErrors(serverErrors(err)) });
          }}
        >
          <PracticeStep draft={draft} update={update} errors={errors} askName />
          <QualificationsStep draft={draft} update={update} errors={errors} />
          <SaveRow pending={save.isPending} saved={save.isSuccess} error={save.error} label={t("profile.saveDetails")} />
        </form>
      </CardContent>
    </Card>
  );
}

function ChambersCard({ orgId, practice }: { orgId: string; practice: Practice }) {
  const t = useT();
  const { api } = useBrandex();
  const [draft, setDraft] = useState<OnboardingDraft>(() => practiceToDraft(practice));
  const [errors, setErrors] = useState<StepErrors>({});
  const save = usePracticeUpdate<OnboardingDraft>(orgId, async (d) =>
    (await api.http.PUT("/v1/orgs/{org_id}/practice/chambers", { params: { path: { org_id: orgId } }, body: toChambersRequest(d) })).data,
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.chambers")}</CardTitle>
        <CardDescription>{t("profile.chambersHint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          noValidate
          className="grid gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            // Same rules as the onboarding chambers step.
            const v = validateStep("chambers", draft);
            setErrors(v);
            if (Object.keys(v).length === 0) save.mutate(draft, { onError: (err) => setErrors(serverErrors(err)) });
          }}
        >
          <ChambersStep
            draft={draft}
            update={(patch) => {
              save.reset();
              setDraft((d) => ({ ...d, ...patch }));
            }}
            errors={errors}
          />
          <SaveRow pending={save.isPending} saved={save.isSuccess} error={save.error} label={t("profile.saveChambers")} />
        </form>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------- FAQ

function useFaq(orgId: string) {
  const { api } = useBrandex();
  return useQuery<Faq[]>({
    queryKey: keys.faq(orgId),
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/faq", { params: { path: { org_id: orgId } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
}

const FAQ_TONE: Record<Faq["status"], "success" | "warning" | "neutral"> = {
  approved: "success",
  draft: "warning",
  needs_answer: "neutral",
};

function FaqCard({ orgId }: { orgId: string }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const faq = useFaq(orgId);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.faq(orgId) });
    void qc.invalidateQueries({ queryKey: keys.practice(orgId) });
  };
  const suggest = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/orgs/{org_id}/faq/suggestions", { params: { path: { org_id: orgId } } });
    },
    onSuccess: refresh,
  });
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t("profile.faq")}</CardTitle>
            <CardDescription>{t("profile.faqHint")}</CardDescription>
          </div>
          <Button variant="outline" size="sm" loading={suggest.isPending} onClick={() => suggest.mutate()}>
            <Lightbulb className="h-4 w-4" aria-hidden />
            {t("profile.faqSuggest")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        {faq.isPending ? <Spinner label={t("common.loading")} /> : null}
        {faq.isError ? <Alert tone="error">{errorMessage(faq.error, t)}</Alert> : null}
        {suggest.isError ? <Alert tone="error">{errorMessage(suggest.error, t)}</Alert> : null}
        {faq.data?.length === 0 ? <p className="text-sm text-muted-foreground">{t("profile.faqEmpty")}</p> : null}
        {faq.data?.map((item) => <FaqRow key={`${item.id}-${item.updated_at}`} orgId={orgId} item={item} onSaved={refresh} />)}
        <NewFaq orgId={orgId} onSaved={refresh} />
      </CardContent>
    </Card>
  );
}

function FaqRow({ orgId, item, onSaved }: { orgId: string; item: Faq; onSaved: () => void }) {
  const t = useT();
  const { api } = useBrandex();
  const [answer, setAnswer] = useState(item.answer);
  // Only the doctor approves answers; staff save drafts for them.
  const { staff } = useCurrentOrg();
  const path = { params: { path: { org_id: orgId, faq_id: item.id } } };
  const save = useMutation({
    mutationFn: async (approve: boolean) => {
      await api.http.PUT("/v1/orgs/{org_id}/faq/{faq_id}", { ...path, body: { question: item.question, answer, approve } });
    },
    onSuccess: onSaved,
  });
  const remove = useMutation({
    mutationFn: async () => {
      await api.http.DELETE("/v1/orgs/{org_id}/faq/{faq_id}", path);
    },
    onSuccess: onSaved,
  });
  const changed = answer.trim() !== item.answer;
  return (
    <div className="grid gap-3 rounded-[var(--radius-lg)] border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="font-medium">{item.question}</p>
        <Badge tone={FAQ_TONE[item.status]}>{t(`profile.faqStatus.${item.status}` as MessageKey)}</Badge>
      </div>
      <Field label={t("profile.faqAnswer")} hint={t("profile.faqAnswerHint")}>
        <Textarea rows={3} maxLength={1500} value={answer} onChange={(e) => setAnswer(e.target.value)} />
      </Field>
      {save.isError ? <Alert tone="error">{errorMessage(save.error, t)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{errorMessage(remove.error, t)}</Alert> : null}
      <div className="flex flex-wrap gap-2">
        {staff ? null : (
          <Button
            size="sm"
            variant="accent"
            disabled={answer.trim() === "" || (item.status === "approved" && !changed)}
            loading={save.isPending && save.variables}
            onClick={() => save.mutate(true)}
          >
            {t("profile.faqApprove")}
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={!changed} loading={save.isPending && !save.variables} onClick={() => save.mutate(false)}>
          {t("profile.faqSaveDraft")}
        </Button>
        <Button size="sm" variant="ghost" loading={remove.isPending} aria-label={t("profile.faqRemove", { question: item.question })} onClick={() => remove.mutate()}>
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function NewFaq({ orgId, onSaved }: { orgId: string; onSaved: () => void }) {
  const t = useT();
  const { api } = useBrandex();
  const [question, setQuestion] = useState("");
  const add = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/orgs/{org_id}/faq", { params: { path: { org_id: orgId } }, body: { question: question.trim() } });
    },
    onSuccess: () => {
      setQuestion("");
      onSaved();
    },
  });
  return (
    <form
      className="grid gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (question.trim().length >= 5) add.mutate();
      }}
    >
      <Field label={t("profile.faqNew")}>
        <Input value={question} maxLength={200} placeholder={t("profile.faqNewPlaceholder")} onChange={(e) => setQuestion(e.target.value)} />
      </Field>
      {add.isError ? <Alert tone="error">{errorMessage(add.error, t)}</Alert> : null}
      <div>
        <Button type="submit" size="sm" variant="outline" disabled={question.trim().length < 5} loading={add.isPending}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("profile.faqAdd")}
        </Button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------ checklists

function Checklist({ items }: { items: { label: string; done: boolean }[] }) {
  return (
    <ul className="grid gap-2 text-sm">
      {items.map((i) => (
        <li key={i.label} className="flex items-start gap-2">
          {i.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
          <span className={i.done ? "" : "text-muted-foreground"}>{i.label}</span>
          <span className="sr-only">{i.done ? "✓" : "○"}</span>
        </li>
      ))}
    </ul>
  );
}

function SideCard({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{hint}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function CompletenessCard({ practice }: { practice: Practice }) {
  const t = useT();
  return (
    <SideCard title={t("profile.completeTitle")} hint={t("profile.completeHint")}>
      <Checklist items={practice.completeness.steps.map((s) => ({ label: t(`profile.step.${s.key}` as MessageKey), done: s.done }))} />
    </SideCard>
  );
}

function VisibilityCard({ practice }: { practice: Practice }) {
  const t = useT();
  return (
    <SideCard title={t("profile.visibilityTitle")} hint={t("profile.visibilityHint")}>
      <Checklist items={practice.completeness.visibility.map((s) => ({ label: t(`profile.check.${s.key}` as MessageKey), done: s.done }))} />
    </SideCard>
  );
}
